import { useCatchEventCallback } from '@pique/core/hooks/use-catch-event-hook';
import { isNewTabTrigger } from '@pique/core/utils';
import {
  FeatureFlagService,
  useLiveData,
  useServices,
} from '@toeverything/infra';
import { type To } from 'history';
import type { MouseEvent, Ref } from 'react';

import { WorkbenchService } from '../services/workbench';

export type WorkbenchLinkProps = React.PropsWithChildren<
  {
    to: To;
    onClick?: (e: MouseEvent) => void;
    replaceHistory?: boolean;
  } & React.HTMLProps<HTMLAnchorElement>
>;

export function WorkbenchLink({ ref, to, onClick, replaceHistory, ...other }: WorkbenchLinkProps & { ref?: Ref<HTMLAnchorElement> }) {
    const { featureFlagService, workbenchService } = useServices({
      FeatureFlagService,
      WorkbenchService,
    });
    const enableMultiView = useLiveData(
      featureFlagService.flags.enable_multi_view.$
    );
    const workbench = workbenchService.workbench;
    const basename = useLiveData(workbench.basename$);
    const link =
      basename +
      (typeof to === 'string' ? to : `${to.pathname}${to.search}${to.hash}`);
    const handleClick = useCatchEventCallback(
      async (event: React.MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) {
          return;
        }
        const at = (() => {
          if (isNewTabTrigger(event)) {
            return 'new-tab';
          }
          return 'active';
        })();
        workbench.open(to, { at, replaceHistory });
        event.preventDefault();
      },
      [enableMultiView, onClick, replaceHistory, to, workbench]
    );

    // eslint suspicious runtime error
    // eslint-disable-next-line react/no-danger-with-children
    return (
      <a
        {...other}
        ref={ref}
        href={link}
        onClick={handleClick}
        onAuxClick={handleClick}
      />
    );
}
